/**
 * NotesHandoffPage
 * Post-call page for the LT-connected lane, reached from the follow-up SMS:
 * /handoff/notes/:token?action=claim|release
 *
 * Same shell and hero as the claim landing page — an agent moving between the
 * two should feel one flow. What is different is the question being asked, and
 * that this is the one page in the flow with something to write up: the call
 * just happened. Notes sit inline, under the summary the agent is already
 * reading, rather than inside a dialog.
 *
 * Both actions submit on the first tap. The agent tapped a link that named the
 * action — "Take over & Add Notes" or "Give back to Remi" — so the choice was
 * already made in the text; a dialog here would only ask it twice.
 */

import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '@crisellevaldez/speculo-ui';
import { CheckCircle2, RotateCcw } from 'lucide-react';
import { useSmsHandoff } from '../../hooks/handoff/useSmsHandoff';
import { FullPageLoader } from '../../components/common/FullPageLoader';
import { HandoffContactLine } from '../../components/features/handoff/HandoffContactLine';
import { HandoffMetaLine } from '../../components/features/handoff/HandoffMetaLine';
import {
  HandoffCard,
  HandoffPageShell,
  HandoffSectionLabel,
} from '../../components/features/handoff/HandoffPageShell';
import { cn } from '../../utils/cn';
import { getHotnessBadge } from '../../utils/handoffHotness';
import { signalFlagsFrom } from '../../utils/handoffFormat';

const NotesHandoffPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();

  // Get action from URL query param (?action=claim or ?action=release)
  const actionParam = searchParams.get('action');
  const initialAction = actionParam === 'claim' || actionParam === 'release' ? actionParam : null;

  // Notes live on the page, not in the confirmation. The agent has just come
  // off the call and is reading the summary here; a box inside a dialog is the
  // wrong place to write one up.
  const [notes, setNotes] = useState('');

  // Action is determined by URL - no user selection needed
  const action = initialAction;

  const {
    handoffDetails,
    isLoading: handoffLoading,
    error: handoffError,
    handleClaimWithNotes,
    handleReleaseWithNotes,
    isSubmittingNotes,
  } = useSmsHandoff({
    token: token || undefined,
    onSuccess: (contactId) => {
      showToast({
        message: action === 'claim' ? 'Lead claimed successfully!' : 'Lead released successfully!',
        variant: 'success',
      });
      // Pass type and contactId to success page for "View Contact" button
      const successUrl =
        action === 'claim' && contactId
          ? `/handoff/success?type=claimed&contactId=${contactId}`
          : '/handoff/success?type=released';
      navigate(successUrl);
    },
    onError: (error) => {
      showToast({
        message: error || 'Error processing handoff',
        variant: 'error',
      });
      navigate(`/handoff/error?reason=${encodeURIComponent(error || 'Unknown error')}`);
    },
  });

  // Notes stay optional: an agent who just got off the phone should not be
  // blocked from recording the outcome because they have nothing to add.
  const submit = () => {
    const trimmed = notes.trim();
    if (action === 'claim') void handleClaimWithNotes(trimmed);
    else void handleReleaseWithNotes(trimmed);
  };

  if (handoffLoading) {
    return <FullPageLoader bgColor="bg-transparent" dotColor="bg-amber-500" />;
  }

  if (handoffError || !handoffDetails) {
    navigate(`/handoff/error?reason=${encodeURIComponent(handoffError || 'invalid')}`);
    return null;
  }

  // Redirect if no valid action in URL
  if (!action) {
    navigate('/handoff/error?reason=invalid-action');
    return null;
  }

  const isClaim = action === 'claim';
  const hotness = getHotnessBadge(handoffDetails.hotnessLevel);
  const signalChips = signalFlagsFrom(handoffDetails.signals, handoffDetails.reasons);

  return (
    <HandoffPageShell maxWidth="max-w-md sm:max-w-lg lg:max-w-2xl">
      {/* Bottom padding clears the pinned action bar. */}
      <div className="pb-28 sm:pb-4">
        <HandoffCard>
          {/* Hero — same shape as the claim landing page. */}
          <div className="border-b border-foreground/10 px-5 py-5 sm:px-7 sm:py-6 lg:px-9 lg:py-8">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h1 className="text-sm font-semibold text-amber-600 dark:text-amber-400 sm:text-base">
                  How did the call go?
                </h1>
                <p className="mt-1 truncate text-xl font-semibold tracking-tight text-foreground sm:text-2xl lg:text-3xl">
                  {handoffDetails.contactName || '-'}
                </p>
                <HandoffContactLine
                  phone={handoffDetails.contactPhone}
                  location={handoffDetails.location}
                  className="mt-1"
                />
              </div>
              <span
                className={cn(
                  'inline-flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold',
                  hotness.bg,
                  hotness.text,
                  hotness.border
                )}
              >
                {hotness.icon}
                {hotness.label}
              </span>
            </div>

            {/* Always a call. This page exists only after one: the LT-connected
                lane reaches it when a transfer connected, and the in-app path
                schedules it after the agent claims and dials. channelOfOrigin
                is where the *conversation* began, which can be sms — showing
                that here would tell an agent their last contact was a text on
                a page headed "How did the call go?". */}
            <HandoffMetaLine channel="voice" className="mt-4" />
          </div>

          <div className="divide-y divide-foreground/10">
            {signalChips.length > 0 && (
              <section className="px-5 py-4 sm:px-7 sm:py-5 lg:px-9 lg:py-6">
                <HandoffSectionLabel>Signals</HandoffSectionLabel>
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  {signalChips.map((label) => (
                    <span
                      key={label}
                      className="inline-flex items-center rounded-md border border-foreground/15 bg-foreground/[0.04] px-2 py-0.5 text-xs font-medium text-foreground/80 sm:px-2.5 sm:py-1 sm:text-[13px]"
                    >
                      {label}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {handoffDetails.conversationSummary && (
              <section className="px-5 py-4 sm:px-7 sm:py-5 lg:px-9 lg:py-6">
                <HandoffSectionLabel>Conversation summary</HandoffSectionLabel>
                <p className="mt-2.5 border-l-2 border-amber-400/50 pl-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
                  {handoffDetails.conversationSummary}
                </p>
              </section>
            )}

            {/* The reason this page exists. Optional, and inline: the agent is
                already here reading the summary, so this is where the call gets
                written up — not behind the confirmation. */}
            <section className="px-5 py-4 sm:px-7 sm:py-5 lg:px-9 lg:py-6">
              <label htmlFor="handoff-call-notes">
                <HandoffSectionLabel>
                  Call notes <span className="font-normal normal-case tracking-normal">(optional)</span>
                </HandoffSectionLabel>
              </label>
              <textarea
                id="handoff-call-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={isSubmittingNotes}
                placeholder="What happened on the call, and what happens next"
                className="mt-2.5 h-28 w-full resize-none rounded-lg border border-foreground/30 bg-background px-3 py-2 text-sm leading-relaxed shadow-sm placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60 sm:h-32 sm:text-base"
              />
            </section>
          </div>

          {/* Desktop CTA lives in the card; on mobile the pinned bar takes over. */}
          <div className="hidden border-t border-foreground/10 bg-foreground/[0.02] px-5 py-4 sm:block sm:px-7 lg:px-9 lg:py-5">
            <NotesCta isClaim={isClaim} disabled={isSubmittingNotes} onClick={submit} />
          </div>
        </HandoffCard>
      </div>

      {/* Pinned action bar — phone-first, always in thumb reach. */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-foreground/10 bg-card/95 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur sm:hidden">
        <div className="mx-auto max-w-md">
          <NotesCta isClaim={isClaim} disabled={isSubmittingNotes} onClick={submit} />
        </div>
      </div>

    </HandoffPageShell>
  );
};

/** Claim keeps the emerald of the landing page; release is a handing-back. */
const NotesCta: React.FC<{
  isClaim: boolean;
  disabled: boolean;
  onClick: () => void;
}> = ({ isClaim, disabled, onClick }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={cn(
      'inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-60 sm:py-3.5 sm:text-base lg:py-4 lg:text-lg',
      isClaim
        ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-emerald-700 dark:from-emerald-600 dark:to-emerald-700 dark:text-emerald-50 dark:shadow-md dark:shadow-emerald-950/40 dark:hover:from-emerald-500 dark:hover:to-emerald-600'
        : 'bg-red-600 text-white shadow-lg shadow-red-500/20 hover:bg-red-700 dark:bg-red-600 dark:shadow-md dark:shadow-red-950/40 dark:hover:bg-red-500'
    )}
  >
    {isClaim ? (
      <CheckCircle2 className="h-4 w-4 lg:h-5 lg:w-5" />
    ) : (
      <RotateCcw className="h-4 w-4 lg:h-5 lg:w-5" />
    )}
    {isClaim ? 'Claim this lead' : 'Release to Remi'}
  </button>
);

export default NotesHandoffPage;
